import type { DEFAULT_STORY } from "@/shared/constants";
import type { FunnelCompletePayload } from "../funnel-mode";

/**
 * Map the funnel's brand styling onto the story's design system.
 *
 * Two layers, in order. The base is `designRoles` — the roles brand-ai-extract
 * assigned — written into the same slots the old wizard used:
 *
 *   icons      -> primaryColor  (--theme-color-1)
 *   background -> themeColor3   (--theme-color-3)  a theme slot, NOT the page background
 *   title      -> titleColor    (--title-color)
 *
 * On top of that, the Ready step's picks override only the slots the user
 * actually touched, so an untouched run reproduces the AI's own choices.
 *
 * `backgroundColor` (--background-main-color) is deliberately never written:
 * the template's own page background has to survive, which is what the wizard
 * did and what the funnel previously broke by painting it with the palette's
 * light colour.
 */
export const applyStyling = (
  {
    brandDesign,
    designRoles,
    palette,
    headlineFont,
    subtitleFont,
    bodyFont,
    iconsColor,
    accentBackgroundColor,
    titlesColor,
  }: FunnelCompletePayload,
  story: typeof DEFAULT_STORY,
) => {
  // Palette is the pre-roles fallback: only used for slots the AI didn't fill.
  const icons = iconsColor ?? designRoles?.icons ?? palette?.brand;
  const themeBackground =
    accentBackgroundColor ?? designRoles?.background ?? palette?.accent;
  const title = titlesColor ?? designRoles?.title;
  // The light tint slot; storydocThemes keep it a pale near-white.
  const secondary = palette?.light;

  const designSystemV2 = {
    ...story.settings.designSystemV2,
    // Whole-system brand repaint (template path) first, so the wizard's role
    // slots and explicit picks below still win where present.
    ...brandDesign,
    // Design-library slides bind icons/elements to --theme-color-4 as often
    // as -1 (every storydocTheme sets themeColor4 === primaryColor), and the
    // old wizard repainted all four slots — so slot 4 follows the icons pick.
    ...(icons && { primaryColor: icons, themeColor4: icons }),
    ...(themeBackground && { themeColor3: themeBackground }),
    ...(secondary && { secondaryColor: secondary }),
    ...(title && { titleColor: title }),
    // Subtitles follow an explicit Ready pick, but not the AI base — the
    // wizard only ever set titleColor.
    ...(titlesColor && { subtitleColor: titlesColor }),
    ...(headlineFont && {
      titleFont: headlineFont,
      subtitleFont: subtitleFont ?? headlineFont,
    }),
    ...(bodyFont && { paragraphFont: bodyFont, font: bodyFont }),
  };
  return {
    ...story,
    settings: { ...story.settings, designSystemV2 },
  };
};
